'use client';

import { Crown, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';

interface FreePlanLimitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lockedTitle?: string;
  onRequestUpgrade?: () => void;
}

export function FreePlanLimitDialog({ open, onOpenChange, lockedTitle, onRequestUpgrade }: FreePlanLimitDialogProps) {
  const { t } = useI18n();

  const handleOpenChange = (nextValue: boolean) => {
    onOpenChange(nextValue);
  };

  const handleUpgrade = () => {
    if (onRequestUpgrade) {
      onRequestUpgrade();
    } else {
      handleOpenChange(false);
    }
  };

  const title = t('course.freePlanLimit.title', 'Upgrade to unlock this lesson');
  const description = t(
    'course.freePlanLimit.description',
    'Free plan users can access the first 10 idioms. Upgrade your plan to continue learning the rest of the course.'
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="items-center text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Lock className="size-6" />
          </div>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            <span>{description}</span>
            {lockedTitle ? (
              <span className="mt-2 block font-medium text-foreground">{lockedTitle}</span>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:flex-row">
          <Button variant="outline" className="flex-1" onClick={() => handleOpenChange(false)}>
            {t('common.gotIt', 'Got it')}
          </Button>
          <Button className="flex-1 gap-2" onClick={handleUpgrade}>
            <Crown className="size-4" />
            {t('user.actions.upgrade', 'Upgrade now')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
